import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import { useAttachmentUrl } from 'hooks/useAttachmentUrl';
import { useClickAttachment } from 'hooks/useClickAttachment';
import { SummaryAttachment } from 'models/aiArtifacts/Summary';
import colors from 'styles/theme/colors';

import { formatProvenance } from './provenanceText';

const CitationPdfPreview = lazy(() => import('./CitationPdfPreview'));

export type CitationBadgeVariant = 'purple' | 'white';

type CitationBadgeProps = {
  attachment: SummaryAttachment;
  size?: number;
  // using a negative because most components will want to show the hover effect
  hideHoverEffect?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  attachmentType?: string;
  /**
   * Pages within the cited PDF to render in the hover preview, in citation order.
   * When undefined or empty, the badge falls back to a simple filename tooltip.
   */
  pages?: number[];
};

export default function CitationBadge({
  attachment,
  size = 16,
  hideHoverEffect = false,
  clickable = true,
  onClick,
  attachmentType,
  pages,
}: CitationBadgeProps) {
  const firstPage = pages?.[0];
  const openAttachment = useClickAttachment(attachment.attachmentId, attachmentType, firstPage);
  const handleClick = onClick ?? openAttachment;
  // Native title still shown to mobile/tablet users where hover preview isn't reachable.
  const tooltipText = attachment.filename ?? undefined;

  return (
    <StyledCitationBadge
      size={size}
      hoverable={!hideHoverEffect}
      clickable={clickable || !!onClick}
      variant='purple'
      title={tooltipText}
      onClick={clickable || !!onClick ? handleClick : undefined}>
      {attachment.displayId}
      {!hideHoverEffect && <PreviewLayer source={attachment} attachmentType={attachmentType} pages={pages} />}
    </StyledCitationBadge>
  );
}

type PreviewProps = {
  source: SummaryAttachment;
  attachmentType?: string;
  pages?: number[];
};

function formatPageRanges(pages: number[]): string {
  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    const current = sorted[i];
    if (current === prev + 1) {
      prev = current;
      continue;
    }
    ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
    start = current;
    prev = current;
  }
  return ranges.join(', ');
}

const CARD_WIDTH = 380;
const CARD_PADDING = 12;
const SCROLL_AREA_WIDTH = CARD_WIDTH - CARD_PADDING * 2;
const MULTI_PAGE_WIDTH = Math.round(SCROLL_AREA_WIDTH * 0.8);

function PreviewLayer({ source, attachmentType, pages }: PreviewProps) {
  const isPdf = /\.pdf$/i.test(source.filename ?? '');
  const pdfPages = pages ?? [];
  const showPdf = isPdf && pdfPages.length > 0;
  const { url, fetch } = useAttachmentUrl(source.attachmentId, attachmentType);
  const provenanceLine = formatProvenance(source);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const gap = 10;

  const cancelHide = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => setVisible(false), 120);
  };

  const handleEnter = () => {
    cancelHide();
    if (showPdf) fetch();
    if (!triggerRef.current) return;
    const parent = triggerRef.current.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const y = rect.bottom + gap;
    let x = rect.left + rect.width / 2 - CARD_WIDTH / 2;
    x = Math.max(8, Math.min(x, window.innerWidth - CARD_WIDTH - 8));
    setCoords({ x, y });
    setVisible(true);
  };

  useEffect(() => () => cancelHide(), []);

  return (
    <>
      <HoverArea ref={triggerRef} onMouseEnter={handleEnter} onMouseLeave={scheduleHide} />
      {visible &&
        createPortal(
          <PreviewCard
            style={{ top: coords.y, left: coords.x, width: CARD_WIDTH }}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}>
            <HoverBridge $gap={gap} />
            <Caption>
              {showPdf
                ? pdfPages.length === 1
                  ? `Page ${pdfPages[0]} of ${source.filename}`
                  : `Pages ${formatPageRanges(pdfPages)} of ${source.filename}`
                : source.filename}
            </Caption>
            {provenanceLine && <Provenance>{provenanceLine}</Provenance>}
            {showPdf && url && (
              <Suspense fallback={<LoadingSpinner />}>
                <CitationPdfPreview
                  attachmentId={source.attachmentId}
                  url={url}
                  pages={pdfPages}
                  width={pdfPages.length > 1 ? MULTI_PAGE_WIDTH : SCROLL_AREA_WIDTH}
                  filename={source.filename}
                />
              </Suspense>
            )}
            {showPdf && !url && <LoadingSpinner />}
          </PreviewCard>,
          document.body
        )}
    </>
  );
}

const purpleStyles = css`
  background: ${colors.ai20};
  color: ${colors.purpleExtraDark};
  border-radius: 26px;
`;

const whiteStyles = css`
  background: ${colors.white};
  color: ${colors.black100};
  border: 0.5px solid ${colors.black25};
  border-radius: 26px;

  &:hover {
    background: ${colors.blue5};
    border-color: ${colors.blue100};
  }
`;

export const StyledCitationBadge = styled.span<{
  size: number;
  hoverable: boolean;
  clickable: boolean;
  variant: CitationBadgeVariant;
}>`
  position: relative;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 8px;
  font-size: 8px;
  font-weight: 500;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  ${({ variant }) => (variant === 'white' ? whiteStyles : purpleStyles)}
  ${({ hoverable, variant }) =>
    hoverable &&
    variant === 'purple' &&
    css`
      &:hover {
        border: 0.5px solid ${colors.purpleExtraDark};
      }
    `}
`;

const HoverArea = styled.span`
  position: absolute;
  inset: 0;
`;

const PreviewCard = styled.div`
  position: fixed;
  z-index: 9999;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HoverBridge = styled.span<{ $gap: number }>`
  position: absolute;
  left: 0;
  right: 0;
  height: ${({ $gap }) => $gap}px;
  top: -${({ $gap }) => $gap}px;
  background: transparent;
`;

function LoadingSpinner() {
  return (
    <LoadingWrapper>
      <CircularProgress color={colors.purple100} size={20} thickness={2} />
    </LoadingWrapper>
  );
}

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;

const Caption = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: ${colors.black100};
`;

const Provenance = styled.div`
  font-style: italic;
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
  color: ${colors.black50};
  margin-top: -4px;
`;
